import React, { useState, useEffect } from "react";
import Dropzone from "../../../components/Dropzone";
import { toast } from "react-toastify";
import Select from "react-select";
import axios from "axios";
import { URL, MEDIA_URL, getToken } from "../../../utils";
import NumberInput from "../../../components/NumberInput";
import { usePermissions } from "../../../hooks/usePermissions";
import { customSelectStyles } from "../../../utils/selectStyles";
import { getCountryCode } from "../../../utils/countryUtils";

// Fonction utilitaire pour valider les données du formulaire
const validateFormData = (data, isWarehouse) => {
  const errors = {};

  if (!data.nom || data.nom.trim() === "") {
    errors.nom = "Le nom est obligatoire";
  }

  if (!data.quartier || data.quartier.trim() === "") {
    errors.quartier = "Le quartier est obligatoire";
  }

  if (!data.country || data.country.trim() === "") {
    errors.country = "Le pays est obligatoire";
  }

  if (!isWarehouse) {
    const objectifJournalier = parseFloat(data.objectif_journalier);
    const objectifMensuel = parseFloat(data.objectif_mensuel);

    if (isNaN(objectifJournalier) || objectifJournalier <= 0) {
      errors.objectif_journalier =
        "L'objectif journalier doit être un nombre positif";
    }

    if (isNaN(objectifMensuel) || objectifMensuel <= 0) {
      errors.objectif_mensuel =
        "L'objectif mensuel doit être un nombre positif";
    }

    if (objectifMensuel < objectifJournalier) {
      errors.objectif_mensuel =
        "L'objectif mensuel doit être supérieur à l'objectif journalier";
    }
  }

  return errors;
};

const AddEditModal = ({
  modalType,
  currentPoint,
  setCurrentPoint,
  closeModal,
  fetchData,
  loading,
  setLoading,
  isWarehouse = false,
}) => {
  const token = getToken();
  const [file, setFile] = useState(null); // État pour stocker le fichier image
  const { hasPermission } = usePermissions();
  const [countries, setCountries] = useState([
    { value: "", label: "--Selectionnez un pays" },
  ]);

  // Vérification des permissions
  const canCreate = isWarehouse
    ? hasPermission("canCreateWarehouse")
    : hasPermission("canCreateStore");

  const canEdit = isWarehouse
    ? hasPermission("canEditWarehouse")
    : hasPermission("canEditStore");

  // Si l'utilisateur n'a pas les permissions, on ferme le modal
  React.useEffect(() => {
    if (modalType === "add" && !canCreate) {
      toast.error(
        "Vous n'avez pas les permissions pour créer " +
          (isWarehouse ? "un entrepôt" : "un store"),
      );
      closeModal();
    } else if (modalType === "edit" && !canEdit) {
      toast.error(
        "Vous n'avez pas les permissions pour modifier " +
          (isWarehouse ? "cet entrepôt" : "ce store"),
      );
      closeModal();
    }
  }, [modalType, canCreate, canEdit, isWarehouse, closeModal]);

  const handleFileUpload = (uploadedFile) => {
    setFile(uploadedFile);
    setCurrentPoint((prev) => ({ ...prev, photo: uploadedFile }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation côté client
    const errors = validateFormData(currentPoint, isWarehouse);
    if (Object.keys(errors).length > 0) {
      // Afficher les erreurs de validation
      Object.values(errors).forEach((errorMessage) => {
        toast.error(errorMessage);
      });
      setLoading(false);
      return;
    }

    const endpoint = `${
      modalType === "add"
        ? isWarehouse
          ? "create-entrepot"
          : "create-magasin"
        : isWarehouse
          ? `edit-entrepot/${currentPoint.id}`
          : `edit-magasin/${currentPoint.id}`
    }`;
    const method = modalType === "add" ? "post" : "put";

    // Utiliser FormData pour envoyer les données avec le fichier
    const formData = new FormData();
    formData.append("nom", currentPoint.nom);
    formData.append("quartier", currentPoint.quartier);
    formData.append("pays", currentPoint.country || "TG");

    if (!isWarehouse) {
      // Champs spécifiques aux points de vente
      // S'assurer que les valeurs sont bien des nombres
      const objectifJournalier =
        parseFloat(currentPoint.objectif_journalier) || 0;
      const objectifMensuel = parseFloat(currentPoint.objectif_mensuel) || 0;

      formData.append("objectif_mensuel", objectifMensuel);
      formData.append("objectif_journalier", objectifJournalier);
    }

    formData.append("is_active", true);
    if (file) {
      formData.append("photo", file);
    }

    try {
      const response = await axios[method](
        `${URL}Magasins/${endpoint}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Vérifier explicitement la structure de la réponse
      if (!response || typeof response !== "object") {
        toast.error("Réponse invalide du serveur");
        console.error("Réponse invalide:", response);
        return;
      }

      // Vérifier si la réponse contient des erreurs malgré un statut de succès
      if (response.data && response.data.error) {
        toast.error(`Erreur: ${response.data.error}`);
        console.error("Erreur dans la réponse:", response.data);
        return;
      }

      if (response.status === (modalType === "add" ? 201 : 200)) {
        fetchData();
        closeModal();
        toast.success(
          `${isWarehouse ? "Entrepôt" : "Store"} ${
            modalType === "add" ? "ajouté" : "modifié"
          } avec succès !`,
        );
      } else {
        // Statut inattendu
        toast.warning(
          `Opération terminée avec un statut inattendu: ${response.status}`,
        );
        console.warn("Réponse avec statut inattendu:", response);
      }
    } catch (err) {
      // Gestion améliorée des erreurs
      if (err.response) {
        // Le serveur a répondu avec un code d'état en dehors de la plage 2xx
        const errorData = err.response.data;

        if (errorData.error) {
          // Message d'erreur spécifique de l'API
          toast.error(`Erreur: ${errorData.error}`);
        } else if (errorData.detail) {
          // Format d'erreur alternatif (utilisé par certaines APIs)
          toast.error(`Erreur: ${errorData.detail}`);
        } else if (errorData.errors && typeof errorData.errors === "object") {
          // Erreurs de validation multiples (format courant dans les APIs)
          const errorMessages = Object.values(errorData.errors).flat();
          errorMessages.forEach((msg) => toast.error(`Validation: ${msg}`));
        } else if (errorData.message) {
          // Format d'erreur alternatif (message simple)
          toast.error(`Erreur: ${errorData.message}`);
        } else if (err.response.status === 401) {
          toast.error("Session expirée. Veuillez vous reconnecter.");
        } else if (err.response.status === 403) {
          toast.error(
            "Vous n'avez pas les droits pour effectuer cette action.",
          );
        } else if (err.response.status === 404) {
          toast.error("La ressource demandée n'existe pas.");
        } else if (err.response.status === 500) {
          toast.error("Erreur serveur. Veuillez réessayer plus tard.");
        } else {
          // Si aucun format d'erreur reconnu n'est détecté, afficher une erreur générique avec le contenu
          toast.error(
            `Erreur (${err.response.status}): ${JSON.stringify(
              errorData,
            ).substring(0, 100)}...`,
          );
        }
      } else if (err.request) {
        toast.error("Aucune réponse du serveur. Vérifiez votre connexion.");
      } else {
        toast.error(
          `Erreur: ${
            err.message ||
            "Erreur inconnue lors de la préparation de la requête"
          }`,
        );
      }

      // Journaliser l'erreur complète pour le débogage
      console.error("Détails de l'erreur:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        stack: err.stack,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(URL + "Clients/countries-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCountries(
        response.data.map((country) => ({
          value: country.code,
          label: country.name,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des pays :", error);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchCountries();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (
      modalType === "edit" &&
      countries.length > 1 &&
      !currentPoint?.country &&
      currentPoint?.pays
    ) {
      const code = getCountryCode(currentPoint.pays, countries);
      setCurrentPoint((prev) => ({ ...prev, country: code }));
    }
  }, [
    modalType,
    countries,
    currentPoint?.country,
    currentPoint?.pays,
    setCurrentPoint,
  ]);

  return (
    <div className="fixed top-0 left-0 inset-0 overflow-auto z-50 flex items-center py-8">
      <div
        className="absolute top-0 left-0 inset-0 h-full bg-black bg-opacity-30 backdrop-blur-sm"
        onClick={closeModal}
      />

      <div className="min-h-max bg-white z-50 mx-auto my-10 rounded-md p-4 shadow-lg w-max max-w-lg">
        <div className="flex gap-4 relative">
          <svg
            className={`w-8 h-8 ${
              modalType === "edit" ? "text-amber-500" : "text-green-500"
            }`}
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeWidth="2"
              d="M3 15v3c0 .5523.44772 1 1 1h8v-8m-9 4v-4m0 4h9m-9-4V6c0-.55228.44772-1 1-1h16c.5523 0 1 .44772 1 1v4M3 11h11m6.25 5c0 1.2426-1.0073 2.25-2.25 2.25M20.25 16c0-1.2426-1.0073-2.25-2.25-2.25M20.25 16H21m-3 2.25c-1.2426 0-2.25-1.0074-2.25-2.25M18 18.25V19m-2.25-3c0-1.2426 1.0074-2.25 2.25-2.25M15.75 16H15m3-2.25V13m-1.591 1.409-.5303-.5303m4.2426 4.2426-.5303-.5303m-3.182 0-.5303.5303m4.2426-4.2426-.5303.5303"
            />
          </svg>

          <div className="text-sm">
            <h3 className="font-semibold">
              {modalType === "add" ? "Ajouter" : "Modifier"} un{" "}
              {isWarehouse ? "entrepôt" : "store"}
            </h3>
            <p className="text-xs">
              {modalType === "add"
                ? `Ajouter un nouveau ${isWarehouse ? "entrepôt" : "store"}`
                : `Modifier ${isWarehouse ? "l'entrepôt" : "le store"}`}
            </p>
          </div>

          <button
            onClick={closeModal}
            className="w-10 h-10 rounded-full bg-gray-200 grid justify-center items-center absolute -top-1 right-0 border border-gray-300 text-gray-600 hover:bg-gray-300 transition-colors"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 py-2 mt-4 w-max max-w-full sm:min-w-96"
        >
          <Dropzone
            accept={{
              "image/png": [".png"],
              "image/jpeg": [".jpg", ".jpeg"],
              "image/gif": [".gif"],
              "image/svg+xml": [".svg"],
            }}
            message="Ajouter une photo"
            textColor={modalType === "add" ? "green" : "orange"}
            onFileUpload={handleFileUpload}
            maxSize={2 * 1024 * 1024} // 2MB
          />

          {/* Prévisualisation de l'image existante (pour le mode edit) */}
          {modalType === "edit" && currentPoint.photo && !file && (
            <div className="mt-2">
              <p className="text-sm text-gray-500">Image actuelle :</p>
              <img
                src={`${MEDIA_URL}${currentPoint.photo}`}
                alt="Actuelle"
                className="mt-1 h-24 w-24 object-cover rounded-md"
              />
            </div>
          )}

          <div className="space-y-4">
            <FormField
              label={`Nom ${isWarehouse ? "de l'entrepôt" : "du store"}`}
              id="nom"
              value={currentPoint.nom || ""}
              onChange={(e) =>
                setCurrentPoint((prev) => ({ ...prev, nom: e.target.value }))
              }
              placeholder={
                isWarehouse ? "Entrepôt central" : "Store de djidjolé"
              }
              required
            />

            <FormField
              label="Quartier"
              id="quartier"
              value={currentPoint.quartier || ""}
              onChange={(e) =>
                setCurrentPoint((prev) => ({
                  ...prev,
                  quartier: e.target.value,
                }))
              }
              placeholder="Quartier - Ville"
              required
            />

            {/* Country */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Pays <span className="text-red-500">*</span>
              </label>
              <Select
                options={countries}
                isLoading={loading}
                onChange={(option) =>
                  setCurrentPoint((prev) => ({
                    ...prev,
                    country: option.value,
                  }))
                }
                value={
                  countries.find(
                    (country) => country.value === currentPoint.country,
                  ) || { value: "", label: "--Selectionnez un pays --" }
                }
                placeholder="Sélectionnez un pays..."
                noOptionsMessage={() => "Aucun résultat"}
                styles={customSelectStyles}
                isSearchable
              />
            </div>

            {/* Champs conditionnels selon le type */}
            {isWarehouse ? null : (
              // Champs pour store
              <div className="flex items-center gap-4">
                <div className="space-y-1">
                  <label htmlFor="objectif_journalier">
                    Objectif journalier
                  </label>
                  <NumberInput
                    label="Objectif journalier"
                    name="objectif_journalier"
                    id="objectif_journalier"
                    value={currentPoint.objectif_journalier || ""}
                    onChange={(value) =>
                      setCurrentPoint((prev) => ({
                        ...prev,
                        objectif_journalier: value,
                      }))
                    }
                    placeholder="100"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label htmlFor="objectif_mensuel">Objectif mensuel</label>
                  <NumberInput
                    label="Objectif mensuel"
                    name="objectif_mensuel"
                    id="objectif_mensuel"
                    value={currentPoint.objectif_mensuel || ""}
                    onChange={(value) =>
                      setCurrentPoint((prev) => ({
                        ...prev,
                        objectif_mensuel: value,
                      }))
                    }
                    placeholder="1000"
                    required
                  />
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-4 grid-cols-2 mt-6">
            <button
              type="button"
              onClick={closeModal}
              className="p-2 rounded-md border-2 bg-gray-100 hover:bg-gray-300 transition-colors"
              disabled={loading}
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={loading}
              className={`p-2 rounded-md border-2 text-white cursor-pointer ${
                modalType === "edit"
                  ? "bg-amber-500 hover:bg-amber-600"
                  : "bg-green-500 hover:bg-green-600"
              } transition-colors flex justify-center items-center`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white rounded-full animate-spin" />
              ) : modalType === "add" ? (
                "Ajouter"
              ) : (
                "Modifier"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FormField = ({
  label,
  id,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
}) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className="text-sm font-medium">
      {label}
    </label>
    <input
      type={type}
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="p-2 border rounded-md focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
    />
  </div>
);

export default React.memo(AddEditModal);
